import { Button } from '@susu/ui/components'

import { useLabel } from '../../../i18n'
import type { OrderItemCustomMadeDetails } from '../types'

interface CustomMadeDetailsModalProps {
  customMadeDetails: OrderItemCustomMadeDetails
  name: string
  /**
   * In-store reorder handler. When provided, a Reorder CTA is shown. The
   * consumer (Customer Hub) decides when to pass it — Style Advisor only, and
   * only when the line has a configuration id (RP-5643).
   */
  onReorder?: () => void
}

export default function CustomMadeDetailsModal({
  customMadeDetails,
  name,
  onReorder,
}: CustomMadeDetailsModalProps) {
  const t = useLabel()
  const { imageUrl, fabricName, size, fitName } = customMadeDetails

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-16 lg:px-20 3xl:px-120 pt-20 pb-24">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-24">
          <span className="text-base leading-base font-medium text-text">
            {t('orderList.customMade.fabric')}
          </span>
          {fabricName && (
            <span className="text-sm leading-sm font-light text-text">
              {fabricName}
            </span>
          )}
        </div>

        {/* Product card */}
        <div className="flex gap-16">
          {/* Image */}
          <div className="w-[130px] h-[156px] flex-shrink-0 overflow-hidden rounded-sm bg-surface-secondary">
            {imageUrl && (
              <img
                src={imageUrl}
                alt={name}
                loading="lazy"
                className="h-full w-full object-contain object-center"
              />
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col gap-4 justify-start">
            <span className="text-base leading-base font-medium text-text">
              {name}
            </span>
            {size && (
              <span className="text-sm leading-sm font-light text-text">
                Size {size}
              </span>
            )}
            {fitName && (
              <span className="text-sm leading-sm font-light text-text">
                {fitName}
              </span>
            )}
          </div>
        </div>
      </div>

      {onReorder && (
        <div className="shrink-0 border-t border-border px-16 lg:px-20 3xl:px-120 py-16">
          <Button
            size="l"
            variant="filled"
            onClick={onReorder}
            className="w-full"
            data-testid="cm-reorder-button"
          >
            {t('orderList.customMade.reorder')}
          </Button>
        </div>
      )}
    </div>
  )
}
